import { useState } from 'react';

import { useAuth } from '../contexts/AuthContext';
import logoImage from '../images/MicroFeedback_Text_Logo.png';

export function OnboardingFlow() {
  const { user } = useAuth();
  const [copiedSiteId, setCopiedSiteId] = useState(false);
  const [copiedWidget, setCopiedWidget] = useState(false);

  // Generate a simple site ID for demonstration
  const siteId = `${user?.uid?.slice(0, 8) || 'demo'}-site-1`;

  const widgetCode = `<!-- MicroFeedback Widget -->
<script>
  window.microfeedbackConfig = {
    siteId: "${siteId}",
    apiUrl: "https://api.microfb.com"
  };
</script>
<script src="https://cdn.microfb.com/widget.js" async></script>

<!-- Add this where you want the feedback widget to appear -->
<div id="microfeedback-widget"></div>`;

  const handleCopyToClipboard = (text: string, type: 'siteId' | 'widget') => {
    navigator.clipboard.writeText(text);
    if (type === 'siteId') {
      setCopiedSiteId(true);
      setTimeout(() => setCopiedSiteId(false), 2000);
    } else {
      setCopiedWidget(true);
      setTimeout(() => setCopiedWidget(false), 2000);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <div className="text-6xl mb-4">🚀</div>
        <div className="flex justify-center mb-4">
          <img 
            src={logoImage} 
            alt="MicroFeedback" 
            className="h-16 w-auto"
          />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Welcome!
        </h1>
        <p className="text-lg text-gray-600">
          Let&apos;s get you set up with your first feedback widget in just a few minutes.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
        {/* Step 1: Site ID */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                <span className="text-indigo-600 font-semibold">1</span>
              </div>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-gray-900">Your Site ID</h3>
              <p className="text-sm text-gray-500">
                This unique identifier connects your widget to your dashboard
              </p>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-md p-3 mb-4">
            <div className="flex items-center justify-between">
              <code className="text-sm font-mono text-gray-800">{siteId}</code>
              <button
                onClick={() => handleCopyToClipboard(siteId, 'siteId')}
                className="ml-2 text-sm text-indigo-600 hover:text-indigo-500"
              >
                {copiedSiteId ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
          
          <p className="text-sm text-gray-600">
            Copy this Site ID - you&apos;ll need it for the next step.
          </p>
        </div>

        {/* Step 2: Widget Installation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                <span className="text-indigo-600 font-semibold">2</span>
              </div>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-gray-900">Install Widget</h3>
              <p className="text-sm text-gray-500">
                Add this code to your website&apos;s HTML
              </p>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-md p-3 mb-4 relative">
            <pre className="text-xs text-gray-800 whitespace-pre-wrap overflow-x-auto">
              {widgetCode}
            </pre>
            <button
              onClick={() => handleCopyToClipboard(widgetCode, 'widget')}
              className="absolute top-2 right-2 text-xs text-indigo-600 hover:text-indigo-500 bg-white px-2 py-1 rounded border"
            >
              {copiedWidget ? 'Copied!' : 'Copy Code'}
            </button>
          </div>
          
          <p className="text-sm text-gray-600">
            Paste this code into your website&apos;s HTML, preferably before the closing &lt;/body&gt; tag.
          </p>
        </div>

        {/* Step 3: Start Collecting Feedback */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:col-span-1 lg:col-span-2">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 font-semibold">3</span>
              </div>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-gray-900">Start Collecting Feedback</h3>
              <p className="text-sm text-gray-500">
                Your widget is ready to collect valuable feedback from your users
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="text-center p-4 bg-gray-50 rounded-md">
              <div className="text-2xl mb-2">👍</div>
              <h4 className="font-medium text-gray-900">Upvotes</h4>
              <p className="text-sm text-gray-600">Track positive feedback</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-md">
              <div className="text-2xl mb-2">👎</div>
              <h4 className="font-medium text-gray-900">Downvotes</h4>
              <p className="text-sm text-gray-600">Identify areas for improvement</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-md">
              <div className="text-2xl mb-2">💬</div>
              <h4 className="font-medium text-gray-900">Comments</h4>
              <p className="text-sm text-gray-600">Get detailed user insights</p>
            </div>
          </div>
          
          <div className="bg-indigo-50 border border-indigo-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <div className="text-indigo-500 text-xl">💡</div>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-indigo-800">Pro Tip</h4>
                <p className="text-sm text-indigo-700 mt-1">
                  Once you add the widget to your site, feedback will start appearing in this dashboard. 
                  You can customize the widget appearance and behavior in the settings.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 text-center">
        <p className="text-sm text-gray-500">
          Need help? Check out our{' '}
          <a href="#" className="text-indigo-600 hover:text-indigo-500">
            documentation
          </a>
          {' '}or{' '}
          <button 
            onClick={() => window.alert('Please email us at hello@microfb.com')}
            className="text-indigo-600 hover:text-indigo-500 underline"
          >
            contact support
          </button>
        </p>
      </div>
    </div>
  );
} 